import React, { useState } from 'react';
import { ShoppingBag, ExternalLink, Shirt, CalendarClock, Info, BellRing, BellOff, Users, Send, X } from 'lucide-react';
import { useSpc } from '../../context/SpcContext';

const OLORUN_URL = 'https://olorunsports.com/collections/smart-performance-coaching-online-shop';
const APEX_URL = 'https://apexcustomclothing.com/collections/smart-performance-coaching';

function ShopTile({ testId, tag, tagTone, title, badgeIcon: BadgeIcon, subtitle, description, bullets, cta, url, footnote, children }) {
  return (
    <div className="card span-6 shop-tile" data-testid={testId}>
      <div className="shop-tile-head">
        <div className="shop-tile-icon"><BadgeIcon size={26} /></div>
        <div className="shop-tile-titles">
          <h3>{title}</h3>
          <div className="eyebrow">{subtitle}</div>
        </div>
        <span className={`pill ${tagTone} shop-tile-pill`}>{tag}</span>
      </div>
      <p style={{ marginTop: 14 }}>{description}</p>
      {bullets && (
        <div className="list" style={{ marginTop: 10 }}>
          {bullets.map((b, i) => (
            <div className="item" key={i}>
              <div className="item-main">{b}</div>
            </div>
          ))}
        </div>
      )}
      <a
        className="btn"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        data-testid={`${testId}-cta`}
      >
        {cta} <ExternalLink size={15} />
      </a>
      {footnote && (
        <div className="notice" style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={14} style={{ marginTop: 2, flex: '0 0 auto' }} />
          <span>{footnote}</span>
        </div>
      )}
      {children}
    </div>
  );
}

function OpenWindowModal({ onClose, onConfirm, count }) {
  const [url, setUrl] = useState(APEX_URL);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onConfirm({ url: url.trim() || APEX_URL, note: note.trim(), clear: true });
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <div className="modal-backdrop open" onMouseDown={onClose} data-testid="apex-open-modal">
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div><div className="eyebrow">Race kit</div><h3>Open Apex ordering window</h3></div>
          <button className="modal-x" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="form-grid">
          <p style={{ margin: 0, color: '#c4c8cd' }}>
            This will notify <strong>{count}</strong> interested member{count === 1 ? '' : 's'} in-app and by push,
            then clear the interest list ready for the next window.
          </p>
          <label>
            Apex link
            <input
              data-testid="apex-open-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={APEX_URL}
              required
            />
          </label>
          <label>
            Optional note (deadline, discount code, etc.)
            <textarea
              data-testid="apex-open-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ordering closes Sunday at midnight. Delivery early April."
              rows={3}
            />
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" data-testid="apex-open-submit" disabled={busy || count === 0}>
              <Send size={15} /> {busy ? 'Sending…' : `Notify ${count} member${count === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApexInterestBlock() {
  const { state, toggleApexInterest } = useSpc();
  const apex = state.apexInterest || { count: 0, interested: false };
  const interested = !!apex.interested;
  const others = interested ? apex.count - 1 : apex.count;
  let waitingText;
  if (interested && others === 0) waitingText = "You're the first on the list.";
  else if (interested) waitingText = `You + ${others} other${others === 1 ? '' : 's'} waiting for the next window.`;
  else if (apex.count === 0) waitingText = 'Be the first to be notified when Apex opens.';
  else waitingText = `${apex.count} member${apex.count === 1 ? ' is' : 's are'} waiting for the next window.`;

  return (
    <div className="apex-interest-block">
      <div className="apex-count-line" data-testid="apex-count">
        <span className="apex-count-dot"><Users size={14} /></span>
        <span>{waitingText}</span>
      </div>
      <button
        className={`btn ${interested ? 'secondary' : ''} apex-interest-btn`}
        data-testid="apex-interest-btn"
        onClick={toggleApexInterest}
      >
        {interested ? <BellOff size={17} /> : <BellRing size={17} />}
        <span>{interested ? "You're on the list — tap to remove" : 'Notify me when Apex opens'}</span>
      </button>
    </div>
  );
}

function ApexOpenBanner({ closeDate }) {
  let countdown = null;
  if (closeDate) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const close = new Date(closeDate + 'T00:00:00');
    const days = Math.round((close - today) / 86400000);
    if (days > 1) countdown = `Closes in ${days} days`;
    else if (days === 1) countdown = 'Closes tomorrow!';
    else if (days === 0) countdown = 'Closes today!';
  }
  return (
    <div className="apex-interest-block">
      <div className="notice" style={{ background: 'rgba(66,211,146,.12)', borderColor: 'rgba(66,211,146,.35)' }} data-testid="apex-open-banner">
        <strong>🎉 Ordering is open right now!</strong> Tap "View Apex collection" above to place your order before the window closes.
        {countdown && <div className="pill" style={{ marginTop: 10, background: 'var(--success)', color: '#07140f', fontWeight: 800, display: 'inline-block' }} data-testid="apex-countdown-pill">⏳ {countdown}</div>}
      </div>
    </div>
  );
}

function AdminApexPanel() {
  const { state, openApexWindow, updateSettings } = useSpc();
  const apex = state.apexInterest || { count: 0, members: [] };
  const members = apex.members || [];
  const [modal, setModal] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const isOpen = !!state.settings?.apexOpen;
  const [closeDate, setCloseDate] = useState(state.settings?.apexCloseDate || '');
  const [savingDate, setSavingDate] = useState(false);
  const doOpen = async (payload) => {
    const r = await openApexWindow(payload);
    return !!r;
  };
  const setOpen = async (next) => {
    setSavingToggle(true);
    await updateSettings({ apexOpen: next });
    setSavingToggle(false);
  };
  const saveCloseDate = async () => {
    setSavingDate(true);
    await updateSettings({ apexCloseDate: closeDate });
    setSavingDate(false);
  };
  const closeDateDirty = closeDate !== (state.settings?.apexCloseDate || '');
  return (
    <>
      <div className="section-head" style={{ marginTop: 8 }}>
        <div>
          <div className="eyebrow">Admin</div>
          <h2>Apex ordering window</h2>
          <p>Flip this once members have been notified — it swaps the shop page from "register your interest" to "ordering is open".</p>
        </div>
        <div className="seg" data-testid="apex-open-toggle">
          <button type="button" className={!isOpen ? 'active' : ''} disabled={savingToggle} onClick={() => setOpen(false)} data-testid="apex-toggle-closed">Collecting interest</button>
          <button type="button" className={isOpen ? 'active' : ''} disabled={savingToggle} onClick={() => setOpen(true)} data-testid="apex-toggle-open">Open now</button>
        </div>
      </div>

      {isOpen && (
        <div className="grid">
          <div className="card span-12" data-testid="apex-close-date-panel">
            <strong>Closing date (optional)</strong>
            <p className="meta" style={{ marginTop: 2 }}>Set when the window closes and members will see a "closes in X days" countdown on the open banner.</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} style={{ width: 'auto' }} data-testid="apex-close-date-input" />
              <button type="button" className="btn" disabled={!closeDateDirty || savingDate} onClick={saveCloseDate} data-testid="apex-close-date-save-btn">{savingDate ? 'Saving…' : 'Save'}</button>
              {closeDate && <button type="button" className="btn ghost" disabled={savingDate} onClick={async () => { setCloseDate(''); setSavingDate(true); await updateSettings({ apexCloseDate: '' }); setSavingDate(false); }} data-testid="apex-close-date-clear-btn">Clear</button>}
            </div>
          </div>
        </div>
      )}

      <div className="grid">
        <div className="card span-12" data-testid="apex-admin-panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="shop-tile-icon"><Users size={22} /></div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <strong>{apex.count} interested {apex.count === 1 ? 'member' : 'members'}</strong>
                <div className="eyebrow">Ready to be notified</div>
              </div>
            </div>
            <button
              className="btn"
              data-testid="apex-open-window-btn"
              onClick={() => setModal(true)}
              disabled={apex.count === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Send size={15} /> Notify {apex.count} interested member{apex.count === 1 ? '' : 's'}
            </button>
          </div>

          {members.length === 0 ? (
            <div className="empty" style={{ marginTop: 14 }} data-testid="apex-empty">
              No members have registered interest yet. When they do, they'll show up here.
            </div>
          ) : (
            <div className="list" style={{ marginTop: 14 }}>
              {members.map((m) => (
                <div className="item" key={m.id} data-testid="apex-member">
                  <div className="item-main" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                      {m.avatar ? <img src={m.avatar} alt={m.name} className="avatar-img" /> : (m.name || 'M').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div>
                      <strong>{m.name}</strong>
                      {m.email && <div className="meta">{m.email}</div>}
                    </div>
                  </div>
                  <span className="pill" style={{ opacity: 0.8 }}>waiting</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && <OpenWindowModal onClose={() => setModal(false)} onConfirm={doOpen} count={apex.count} />}
    </>
  );
}

export default function Shop() {
  const { role, state } = useSpc();
  const apexOpen = !!state.settings?.apexOpen;
  return (
    <div data-testid="shop-view">
      <div className="section-head">
        <div>
          <div className="eyebrow">Member shop</div>
          <h2>Club Shops</h2>
          <p>Everyday training kit from Olorun, plus periodic race-day kit windows with Apex.</p>
        </div>
      </div>

      <div className="grid">
        <ShopTile
          testId="shop-olorun"
          tag="Open all year"
          tagTone="green"
          badgeIcon={ShoppingBag}
          title="Olorun Sports"
          subtitle="Smart Performance Coaching collection"
          description="Our year-round training range with Olorun — tees, hoodies, jackets and accessories in the club colours. Order any time, delivered direct."
          bullets={[
            'Year-round availability',
            'Direct-to-door delivery',
            'Club branding on every piece',
          ]}
          cta="Shop Olorun"
          url={OLORUN_URL}
        />

        <ShopTile
          testId="shop-apex"
          tag={apexOpen ? 'Open now' : 'Ordering window'}
          tagTone={apexOpen ? 'green' : 'yellow'}
          badgeIcon={Shirt}
          title="Apex Custom Clothing"
          subtitle="SPC race kit"
          description="Our official race-day kit made by Apex Custom Clothing. Orders run in scheduled windows a few times a year — we open a window when enough members show interest."
          bullets={[
            'Race vests, tops and tri-suits',
            'Ordering opens periodically',
            'Watch Clubhouse notifications for the next window',
          ]}
          cta="View Apex collection"
          url={APEX_URL}
          footnote={apexOpen ? null : (
            <>
              <strong>How it works: </strong>
              You can browse the Apex range any time. Ordering only opens when we announce a window in the Clubhouse — tap the button below to be first to know.
            </>
          )}
        >
          {apexOpen ? <ApexOpenBanner closeDate={state.settings?.apexCloseDate} /> : <ApexInterestBlock />}
        </ShopTile>
      </div>

      {role === 'admin' && <AdminApexPanel />}
    </div>
  );
}
